import { useEffect, useState } from 'react'

/**
 * Die Frage, die das Erscheinungsbild entscheidet.
 *
 * Dieselbe Bedingung steht in `tokens.css` am einzigen
 * `@media (prefers-color-scheme: dark)`-Block, und das ist kein Zufall,
 * sondern die Bedingung: zwei Wahrheiten darüber, wann es dunkel ist, liefen
 * auseinander. Wer sie ändert, ändert beide.
 */
const DUNKEL = '(prefers-color-scheme: dark)'

/**
 * Ob das Gerät gerade auf dunkel steht — **und es geht mit** (#73).
 *
 * **Gebraucht wird das nur, weil Farben einstellbar sind.** Was fest im
 * Stylesheet steht, wechselt der Browser selbst; eine Farbe, die aus einer
 * Einstellung kommt und am Wurzelelement gesetzt wird, kann er nicht kennen.
 * Sie muss umgerechnet werden (`fuerDunkel()` in `lib/kontrast.ts`), und dafür
 * muss jemand wissen, welches Erscheinungsbild gerade gilt.
 *
 * **Zugehört und nicht einmal gemessen.** Ein iPad schaltet abends von selbst
 * um, und eine Web-App vom Home-Bildschirm läuft dabei weiter — dieselbe
 * Beobachtung wie bei der Zeitzone in `lib/format.ts`: ein Wert von damals ist
 * keiner.
 */
export function useDunkel(): boolean {
  const [dunkel, setDunkel] = useState(
    () => typeof window !== 'undefined' && (window.matchMedia?.(DUNKEL).matches ?? false),
  )

  useEffect(() => {
    const frage = window.matchMedia?.(DUNKEL)
    if (frage === undefined) return

    const merke = () => setDunkel(frage.matches)
    merke()
    frage.addEventListener('change', merke)
    return () => frage.removeEventListener('change', merke)
  }, [])

  return dunkel
}
